import { createSelector } from 'reselect'
import * as Schema from './schema'
import { applyDefaults } from '../lib/schema'
import * as Process from './process'

const getRunIndexes = createSelector([ Process.get ], (process) => {
  if (!process) return { start: 0, end: Infinity }
  const startIndex = process.nodes.findIndex(n => n.id === process.runStartNode)
  const endIndex = process.nodes.findIndex(n => n.id === process.runEndNode)
  return {
    start: startIndex > 1 ? startIndex : 0,
    end: endIndex >= 0 ? endIndex : Infinity
  }
})

const INVALID_RUNID = /[^\w-+]+/

export function validateRunID (runID = '') {
  runID = runID.trim()
  if (!runID) return
  let matches = INVALID_RUNID.exec(runID)
  if (!matches) return
  matches = [...new Set(...matches)]
  return `Invalid Character${matches.length > 1 ? 's' : ''}: ${matches.slice(0, 3).join(' ')}`
}

export function isRunIDValid (runID = '') {
  return !INVALID_RUNID.test(runID)
}

export const getAllNodeSchemas = createSelector([ Schema.getAll, Process.get ], (schemas, process) => {
  if (!process) return []
  return process.nodes.map((node, index, stages) => {
    const schema = schemas[node.schemaID]
    return {
      node: {
        ...node,
        value: applyDefaults(schema, { ...node.value })
      },
      schema,
      id: node.id,
      schemaID: schema.id,
      schemaType: schema.type,
      nodeIndex: process.nodes.findIndex(n => n.id === node.id)
    }
  })
})

export const getAll = createSelector([
  Schema.getAll,
  Process.get,
  getAllNodeSchemas,
  getRunIndexes
], (schemas, process, nodeSchemas, runIndexes) => {
  if (!nodeSchemas) return []
  const placed = new Set()
  return (
    nodeSchemas
      .filter(({ schemaType }) => schemaType !== 'analysis')
      .map((nodeSchema, index, stages) => {
        const { node, schema, nodeIndex } = nodeSchema
        return Object.assign({}, nodeSchema, {
          get allInRun () {
            return runIndexes.start <= 1 && runIndexes.end === Infinity
          },
          get isRunStart () {
            if (nodeIndex <= 1 && runIndexes.start <= 1) return true
            return nodeIndex === runIndexes.start
          },
          get isRunEnd () {
            if (runIndexes.end === Infinity && index === stages.length - 1) return true
            return nodeIndex === runIndexes.end
          },
          get canBeRunStart () {
            return nodeIndex !== runIndexes.start && nodeIndex <= runIndexes.end
          },
          get canBeRunEnd () {
            return nodeIndex !== runIndexes.end && nodeIndex >= runIndexes.start
          },
          get isInRun () {
          // always enable input node if starting at first node
            if (nodeIndex === 0 && runIndexes.start === 1) return true
            return nodeIndex >= runIndexes.start && nodeIndex <= runIndexes.end
          },
          get enabled () {
            if (node.disabled) return false
            return this.isInRun
          },
          get runID () {
            // analysis nodes cannot use run identifiers
            if (schema.type === 'analysis') return ''
            return (node.runID || '').trim()
          },
          get isRunIDValid () {
            return isRunIDValid(this.runID)
          },
          get analyses () {
            let done = false
            return process.nodes.slice(nodeIndex + 1).filter(node => {
              if (done) return false
              const schema = schemas[node.schemaID]
              if (schema.type !== 'analysis') {
                if (!node.ignored) done = true
                return false
              }
              if (placed.has(node.id)) return false
              placed.add(node.id)
              return true
            }).map(({ id }) => {
              return nodeSchemas.find(({ node }) => node.id === id)
            })
          }
        })
      })
  )
})



// WEBPACK FOOTER //
// ../webmango-core/selectors/stages.js